import React from 'react';
import { string } from 'prop-types';

/**
 * Service glyphs, keyed by service id.
 *
 * Inline rather than image assets so they inherit `currentColor` and stay on
 * the palette. The previous `assets/services/*.svg` files carried baked-in
 * colours that could not follow the tinting on the AI cards.
 *
 * Shared, because the landing grid and the service pages both show the mark
 * for a service and two copies would drift. Adding a service page without a
 * glyph here renders nothing rather than breaking, so the mark is optional by
 * design — but it looks unfinished, so add one.
 */
const SERVICE_ICONS = {
  aiAgents: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3.2l1.9 4.3 4.3 1.9-4.3 1.9L12 15.6l-1.9-4.3L5.8 9.4l4.3-1.9L12 3.2z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M18.4 15.4l.9 2 2 .9-2 .9-.9 2-.9-2-2-.9 2-.9.9-2z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  ),
  dataMl: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <ellipse cx="12" cy="5.8" rx="7.6" ry="3" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M4.4 5.8v12.4c0 1.66 3.4 3 7.6 3s7.6-1.34 7.6-3V5.8"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path d="M4.4 12c0 1.66 3.4 3 7.6 3s7.6-1.34 7.6-3" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  ),
  customSoftwareDevelopment: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2.5l8 4.6v9.8l-8 4.6-8-4.6V7.1l8-4.6z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M12 12.2l8-4.6M12 12.2v9.3M12 12.2L4 7.6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  ),
  marketplaces: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 5.6h3.2l2.2 9.6h8.4l2.2-6.8H7.4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9.6" cy="19" r="1.5" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="16.6" cy="19" r="1.5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  ),
  webDevelopment: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2.6" y="4" width="18.8" height="14" rx="2.4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M2.6 8.4h18.8M6 20.6h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="5.6" cy="6.2" r=".9" fill="currentColor" />
    </svg>
  ),
  mobileAppDevelopment: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="6.4" y="2.2" width="11.2" height="19.6" rx="2.6" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10.4 5.4h3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="12" cy="18.2" r="1.1" fill="currentColor" />
    </svg>
  ),
  cloudSolutions: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6.6 18.4a4.4 4.4 0 01-.5-8.77 6 6 0 0111.63-1.7 3.9 3.9 0 013.67 3.9 4.4 4.4 0 01-4.4 4.4H6.6z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  ),
  uiUxDesign: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M14.8 3.4l5.8 5.8-9.3 9.3-5.8-5.8 9.3-9.3z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M4.2 19.8l2.9-1 -1.9-1.9-1 2.9z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M12.4 5.8l5.8 5.8" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  ),
  qualityAssurance: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2.8l7 2.6v5.8c0 4.2-2.9 7.7-7 9-4.1-1.3-7-4.8-7-9V5.4l7-2.6z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M8.7 11.9l2.3 2.3 4.3-4.6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  productManagement: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 5.5h10M4 12h16M4 18.5h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="17.5" cy="5.5" r="2.3" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="13.5" cy="18.5" r="2.3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  softwareMaintenance: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 12a8 8 0 01-13.1 6.2M4 12a8 8 0 0113.1-6.2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path d="M4.2 14.9V11h3.9M19.8 9.1V13h-3.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="2.2" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
};

/**
 * The glyph for one service, or nothing when the id has none.
 *
 * @param {object} props
 * @param {string} props.serviceId  key into SERVICE_ICONS
 * @param {string} [props.className]
 */
const ServiceIcon = ({ serviceId, className = null }) => {
  const icon = SERVICE_ICONS[serviceId];
  if (!icon) return null;

  return <span className={className}>{icon}</span>;
};

ServiceIcon.propTypes = {
  serviceId: string.isRequired,
  className: string,
};

export { SERVICE_ICONS };
export default ServiceIcon;
